import type { ComponentType } from 'react';
import * as Sentry from '@sentry/react';
import { loadRemote, registerRemotes } from '@module-federation/runtime';
import sentryTags from 'services/sentry/constants/sentryTags.constants';
import { Config } from 'config';
import store from 'store';
import { accountIdSelector as accountDataIdSelector } from 'store/account/account.selectors';
import {
  accountIdSelector as authAccountIdSelector,
  authIdSelector,
  userRoleSelector
} from 'store/auth/auth.selectors';
import { mfeUrls } from './mfeUrls';

const isTestRuntime = () => {
  return process.env.NODE_ENV === 'test' || Boolean(import.meta.env.VITEST);
};

export const initializeModuleFederationRuntime = (): void => {
  if (isTestRuntime()) {
    return;
  }

  registerRemotes(
    Object.entries(mfeUrls).map(([name, entry]) => ({ name, type: 'module', entry })),
    { force: true }
  );
};

interface PendingMfeError {
  error: Error;
  remoteId: string;
  remoteName: string;
  entryUrl: string;
  originalMessage: string;
}

const MAX_WAIT_FOR_AUTH_MS = 10_000;
const pendingMfeErrors: PendingMfeError[] = [];
let flushTimeoutId: ReturnType<typeof setTimeout> | null = null;
let storeUnsubscribe: (() => void) | null = null;

const readAccountId = () => {
  const state = store.getState() as RootState;
  return accountDataIdSelector(state) ?? authAccountIdSelector(state);
};

const sendMfeErrorToSentry = (pending: PendingMfeError): void => {
  const state = store.getState() as RootState;
  const userId = authIdSelector(state);
  const userRole = userRoleSelector(state);
  const accountId = readAccountId();

  Sentry.withScope((scope) => {
    scope.setTag(sentryTags.mfeName, pending.remoteName);
    scope.setTag(sentryTags.mfeLoadError, 'true');
    if (accountId) {
      scope.setTag('accountId', String(accountId));
    }
    if (userId) {
      scope.setUser({ id: String(userId), role: userRole });
    }
    scope.setContext('mfe-load-error', {
      remoteId: pending.remoteId,
      remoteName: pending.remoteName,
      entryUrl: pending.entryUrl,
      originalMessage: pending.originalMessage,
      release: Config.RELEASE_NUMBER
    });
    scope.setFingerprint(['mfe-load-error', pending.remoteName]);
    Sentry.captureException(pending.error);
  });
};

const flushPendingMfeErrors = () => {
  if (pendingMfeErrors.length === 0) {
    return;
  }
  const queued = pendingMfeErrors.splice(0, pendingMfeErrors.length);
  queued.forEach(sendMfeErrorToSentry);

  if (storeUnsubscribe) {
    storeUnsubscribe();
    storeUnsubscribe = null;
  }
  if (flushTimeoutId !== null) {
    clearTimeout(flushTimeoutId);
    flushTimeoutId = null;
  }
};

const scheduleFlushOnAuth = () => {
  if (storeUnsubscribe || flushTimeoutId !== null) {
    return;
  }
  storeUnsubscribe = store.subscribe(() => {
    if (readAccountId()) {
      flushPendingMfeErrors();
    }
  });
  flushTimeoutId = setTimeout(flushPendingMfeErrors, MAX_WAIT_FOR_AUTH_MS);
};

const captureMfeLoadError = (remoteId: string, originalError: unknown) => {
  const remoteName = remoteId.split('/')[0];
  const entryUrl = mfeUrls[remoteName];
  const originalMessage =
    originalError instanceof Error ? originalError.message : String(originalError);
  const error = new Error(`Failed to load MFE "${remoteId}": ${originalMessage}`, {
    cause: originalError
  });

  const pendingMfeError: PendingMfeError = {
    error,
    remoteId,
    remoteName,
    entryUrl,
    originalMessage
  };

  if (readAccountId()) {
    sendMfeErrorToSentry(pendingMfeError);
  } else {
    pendingMfeErrors.push(pendingMfeError);
    scheduleFlushOnAuth();
  }

  return error;
};

export const loadRemoteModule = async <TModule>(remoteId: string) => {
  if (isTestRuntime()) {
    return { default: () => null } as TModule;
  }

  try {
    const remoteModule = await loadRemote<TModule>(remoteId);

    if (!remoteModule) {
      throw new Error('Remote returned no module');
    }

    return remoteModule;
  } catch (originalError) {
    throw captureMfeLoadError(remoteId, originalError);
  }
};

export const loadRemoteComponent = async <TProps = Record<string, never>>(
  remoteId: string
): Promise<{ default: ComponentType<TProps> }> => {
  return loadRemoteModule<{ default: ComponentType<TProps> }>(remoteId);
};
